import type {
  FeedProblem,
  MachineUpdateView,
  RemoteUpdateBlocker,
  UpdateActivity,
  UpdateChannel,
  UpdateErrorCode,
  UpdateJobStatus,
  UpdateJobView,
} from "@racler/protocol";

/**
 * Remote Desktop updates (V1): pure presentation logic and EVERY user-facing text of the web UI in one place, so the
 * wording stays consistent (and translatable later) and the rules are unit tested without rendering.
 */

const JOB_STATUS_LABELS: Record<UpdateJobStatus, string> = {
  requested: "Requested",
  downloading: "Downloading",
  verifying: "Verifying",
  preparing: "Preparing installation",
  installing: "Installing / restarting",
  awaiting_reconnect: "Waiting for the machine to reconnect",
  succeeded: "Updated",
  failed: "Failed",
  unclear: "Result unclear",
};

export const jobStatusLabel = (status: UpdateJobStatus): string => JOB_STATUS_LABELS[status];

const ERROR_TEXTS: Record<UpdateErrorCode, string> = {
  no_update: "No newer version is available for this machine.",
  target_changed: "A different version is now available. Review the update again.",
  channel_mismatch: "The update channel is still being synchronised. Try again in a moment.",
  update_in_progress: "Another update is already being downloaded or installed on this machine.",
  snapshot_invalid: "The confirmation expired. Review the update again.",
  activity_changed: "New work started on the machine after you confirmed. Nothing was installed.",
  activity_unknown: "The running work on the machine could not be determined. Nothing was installed.",
  feed_unavailable: "The update information could not be loaded. Nothing was installed.",
  download_failed: "The download failed. Nothing was installed.",
  integrity_failed: "The download failed its integrity or signature check. Nothing was installed.",
  install_failed: "The installation failed.",
  update_not_applied: "The machine came back with its previous version; the update was not applied.",
  unexpected_version: "The machine came back with an unexpected version.",
  interrupted: "The desktop app stopped before the update could be installed.",
  timeout: "The update made no progress in time.",
  reconnect_timeout: "The machine did not come back in time. It may still be restarting; check again later.",
  updater_unavailable: "Updates are not available in this build of the app (development or unsigned build).",
};

export const errorText = (code: UpdateErrorCode): string => ERROR_TEXTS[code];

const FINISHED: ReadonlySet<UpdateJobStatus> = new Set(["succeeded", "failed", "unclear"]);

/** A job that is still running (not succeeded / failed / unclear). */
export const isJobActive = (job: UpdateJobView | null | undefined): job is UpdateJobView =>
  !!job && !FINISHED.has(job.status);

export const hasUpdate = (view: MachineUpdateView): boolean =>
  view.availability === "optional" || view.availability === "required";

/** The "Update now" button is offered only when every precondition holds; the server checks all of them again. */
export function canUpdateNow(view: MachineUpdateView): boolean {
  return (
    view.online &&
    view.remoteBlocker === null &&
    view.latest !== null &&
    hasUpdate(view) &&
    !isJobActive(view.job) &&
    view.channelSynced
  );
}

const BLOCKER_TEXTS: Record<RemoteUpdateBlocker, string> = {
  offline: "Offline. Remote updates need the machine to be online.",
  legacy_client:
    "This version of the desktop app cannot be updated remotely yet. Update it once manually on the machine.",
  unsupported_client:
    "This machine runs the racler CLI agent. Update it with your package manager or the CLI.",
  unsupported_arch: "Remote updates are not available for this machine's architecture.",
  updater_unavailable: "Updates are not available in this build of the app (development or unsigned build).",
};

export const blockerText = (blocker: RemoteUpdateBlocker): string => BLOCKER_TEXTS[blocker];

export const MANUAL_UPDATE_TITLE = "How to update this machine once manually";

/** One manual update brings a legacy Desktop to a version that supports remote updates. */
export const manualUpdateSteps: readonly string[] = [
  "Open racler.dev on this Mac (menu bar icon → Settings).",
  "Open Advanced Details & Diagnostics and click Check for Updates… (or use racler.dev → Check for Updates… in the app menu).",
  "Click Download, then Restart and Install.",
  "After this one-time update, the machine can be updated from this page.",
];

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** The work a restart interrupts, one line per non-zero kind. */
export function activityLines(activity: UpdateActivity): string[] {
  const lines: string[] = [];
  if (activity.terminals) lines.push(plural(activity.terminals, "terminal", "terminals"));
  if (activity.agentRuns) lines.push(plural(activity.agentRuns, "agent run", "agent runs"));
  if (activity.runningCommands) {
    lines.push(plural(activity.runningCommands, "running command", "running commands"));
  }
  if (activity.pendingApprovals) {
    lines.push(
      plural(activity.pendingApprovals, "command waiting for approval", "commands waiting for approval"),
    );
  }
  if (activity.transfers) lines.push(plural(activity.transfers, "file upload", "file uploads"));
  if (activity.operations) {
    lines.push(plural(activity.operations, "operation in progress", "operations in progress"));
  }
  return lines;
}

export const UPDATE_WARNING =
  "To install the update, the desktop agent restarts. The machine is temporarily unreachable during this time. Running terminals, agent tasks and commands may be stopped. Unfinished work may be lost.";

export interface HomeUpdateSummary {
  count: number;
  required: number;
  text: string;
  tone: "required" | "optional";
}

/**
 * Home page notice. Only the server's `availability` decides "required" (never the channel). Machines whose update is
 * already running are not counted.
 */
export function homeUpdateSummary(views: Iterable<MachineUpdateView>): HomeUpdateSummary | null {
  let count = 0;
  let required = 0;
  for (const v of views) {
    if (isJobActive(v.job) || !hasUpdate(v)) continue;
    count++;
    if (v.availability === "required") required++;
  }
  if (count === 0) return null;
  let text = count === 1 ? "1 machine has an update available." : `${count} machines have updates available.`;
  if (required > 0) {
    text +=
      required === 1
        ? " 1 machine needs an update to stay compatible."
        : ` ${required} machines need an update to stay compatible.`;
  }
  return { count, required, text, tone: required > 0 ? "required" : "optional" };
}

const FEED_PROBLEM_TEXTS: Record<FeedProblem, string> = {
  unavailable: "Update information is unavailable right now.",
  access_denied: "The update server refused access (configuration problem).",
  invalid: "The update information is invalid.",
};

export const feedProblemText = (problem: FeedProblem): string => FEED_PROBLEM_TEXTS[problem];

/** Availability of the newest allowed version (the feed problem is appended when the state is unknown). */
export function availabilityLabel(view: MachineUpdateView): string {
  switch (view.availability) {
    case "up_to_date":
      return "Up to date";
    case "optional":
      return `Update available: ${view.latest?.version ?? ""}`.trim();
    case "required":
      return `Update required: ${view.latest?.version ?? ""}`.trim();
    case "unknown":
      return view.feedProblem ? `Unknown. ${feedProblemText(view.feedProblem)}` : "Unknown";
  }
}

export const CHANNEL_OPTIONS: readonly { value: UpdateChannel; label: string }[] = [
  { value: "stable", label: "Stable" },
  { value: "beta", label: "Beta (early access)" },
];

export const channelLabel = (channel: UpdateChannel): string => (channel === "beta" ? "Beta" : "Stable");

/** Text and tone of a finished job (succeeded / failed / unclear); null while it is still running. */
export function finishedJobText(job: UpdateJobView): { text: string; tone: "on" | "off" | "warn" } | null {
  switch (job.status) {
    case "succeeded":
      return { text: `Updated to ${job.installedVersion ?? job.targetVersion}`, tone: "on" };
    case "failed":
      return { text: job.errorCode ? errorText(job.errorCode) : "The update failed.", tone: "off" };
    case "unclear":
      return {
        text: job.errorCode ? `Result unclear. ${errorText(job.errorCode)}` : "Result unclear",
        tone: "warn",
      };
    default:
      return null;
  }
}

/** Installed version of a Machine: while offline it is only the last report and may be outdated. */
export function installedVersionText(view: MachineUpdateView): { text: string; outdated: boolean } {
  if (!view.client) return { text: "Version unknown", outdated: false };
  if (view.online) return { text: view.client.version, outdated: false };
  return { text: `Last reported: ${view.client.version}`, outdated: true };
}

const timeOf = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });

/** Remaining texts of the Machines page, the confirmation dialog and the home notice. */
export const updateTexts = {
  updateNow: "Update now",
  checking: "Checking the machine…",
  checkAgain: "Check again",
  cancel: "Cancel",
  syncing: "Syncing…",
  saving: "Saving…",
  loading: "…",
  outdated: "(may be outdated)",
  channelAria: "Update channel",
  dialogTitle: (machineName: string) => `Update ${machineName}?`,
  machine: "Machine",
  installedVersion: "Installed version",
  targetVersion: "New version",
  channel: "Channel",
  runningWork: "Running on this machine:",
  noActivity: "No running terminals, agent runs or commands were found.",
  alreadyDownloaded: "The update is already downloaded on the machine.",
  acknowledge: "I understand that the listed work will be stopped.",
  expired: "This check has expired. Check the machine again before updating.",
  validUntil: (expiresAt: string) => `This check is valid until ${timeOf(expiresAt)}`,
  channelSaveFailed: (message: string) => `Could not change the channel: ${message}`,
  homeReview: "Review updates",
} as const;

/** A relay error (code + message) as a sentence for the dialog. */
export function requestErrorText(err: unknown): string {
  const e = (err ?? {}) as { code?: unknown; message?: unknown };
  if (e.code === "MACHINE_OFFLINE") return "The machine is offline or the connection was lost.";
  if (e.code === "TIMEOUT") return "The machine did not answer in time. Try again.";
  if (e.code === "UNAUTHORIZED") return "Your session has ended. Sign in again.";
  const message = typeof e.message === "string" && e.message ? e.message : "The request failed";
  const sentence = message.charAt(0).toUpperCase() + message.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}

/** The server refused because the confirmation is no longer valid: the user has to check the machine again. */
export function needsRecheck(err: unknown): boolean {
  const e = (err ?? {}) as { code?: unknown; message?: unknown };
  return (
    e.code === "CONFLICT" &&
    typeof e.message === "string" &&
    /review the update again|expired|synchronised/i.test(e.message)
  );
}
